import {useNavigate, useParams} from "react-router-dom";
import {useEffect, useRef, useState} from "react";
import {Leaderboard} from "../data/leaderboard";
import {
    addLeaderboardEntryApiCall,
    deleteLeaderboardApiCall,
    deleteLeaderboardEntryApiCall,
    getLeaderboardApiCall,
    getLeaderboardEntriesApiCall,
    updateLeaderboardApiCall,
} from "../utils/apiCalls";
import {Entry} from "../data/entry";
import {Nullable} from "primereact/ts-helpers";
import {Panel} from "primereact/panel";
import {Button} from "primereact/button";
import LeaderboardGdImplementation from "./LeaderboardGdImplementation";
import Sorting from "../data/sorting";
import LeaderboardEditSidebar from "./LeaderboardEditSidebar";
import EntryEditSidebar from "./EntryEditSidebar";
import EntryView from "./EntryView";
import {ConfirmPopup, confirmPopup} from "primereact/confirmpopup";
import {Toast} from "primereact/toast";
import {InputText} from "primereact/inputtext";

function LeaderboardDetails() {
    const navigate = useNavigate();
    const {uuid} = useParams<{ uuid: string }>();
    const [isLoading, setIsLoading] = useState(false);
    const [entries, setEntries] = useState<Entry[]>([]);
    const [leaderboard, setLeaderboard] = useState<Nullable<Leaderboard>>(null);
    const [leaderboardSidebarVisible, setLeaderboardSidebarVisible] = useState(false);
    const [entrySidebarVisible, setEntrySidebarVisible] = useState(false);
    const toast = useRef<Toast>(null)

    async function fetchEntries() {
        setIsLoading(true);

        try {
            const responseLeaderboard = await getLeaderboardApiCall(uuid!);
            const resLeaderboardData = await responseLeaderboard.json();
            const leaderboard = Object.assign(new Leaderboard(), resLeaderboardData);

            const responseEntries = await getLeaderboardEntriesApiCall(uuid!);
            const resEntriesData = await responseEntries.json();
            const entries = resEntriesData.map((element: any) => {
                return Object.assign(new Entry(), element);
            });

            setLeaderboard(leaderboard);
            setEntries(entries);
        } catch (e) {
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Unable to fetch leaderboard entries.",
                life: 3000,
            });
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        fetchEntries();
    }, []);

    async function onLeaderboardSave(updatedLeaderboard: Leaderboard) {
        setLeaderboardSidebarVisible(false)
        setIsLoading(true);

        try {
            const response = await updateLeaderboardApiCall(updatedLeaderboard)
            const resLeaderboardData = await response.json();
            const updatedLeaderboardResponse = Object.assign(new Leaderboard(), resLeaderboardData);

            setLeaderboard(updatedLeaderboardResponse);
            await fetchEntries();
        } catch (e) {
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Unable to update leaderboard.",
                life: 3000,
            });
        } finally {
            setIsLoading(false);
        }
    }

    async function onEntrySave(updatedEntry: Entry) {
        setEntrySidebarVisible(false)
        setIsLoading(true);

        try {
            await addLeaderboardEntryApiCall(updatedEntry, leaderboard!.secret)
        } catch (e) {
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Unable to update entry.",
                life: 3000,
            });
        }
        await fetchEntries()
    }

    async function onEntryDelete(deletedEntry: Entry) {
        setIsLoading(true);

        try {
            await deleteLeaderboardEntryApiCall(deletedEntry)
        } catch (e) {
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Unable to delete entry.",
                life: 3000,
            });
        }
        await fetchEntries()
    }

    const confirmDelete = (target: any) => {
        const accept = async () => {
            try {
                await deleteLeaderboardApiCall(leaderboard!.id);
                navigate("/")
            } catch (e) {
                toast.current?.show({
                    severity: "error",
                    summary: "Error",
                    detail: "Unable to delete leaderboard.",
                    life: 3000,
                });
            }
        };

        const reject = () => {
        };

        confirmPopup({
            target: target,
            message: `Are you sure that you want to delete the leaderboard ${leaderboard!.name}?`,
            icon: "pi pi-exclamation-triangle",
            defaultFocus: "accept",
            accept,
            reject,
        });
    }

    if (leaderboard == null) {
        return <div
            style={{
                maxWidth: "800px",
                margin: "0 auto",
                padding: "1rem",
                boxSizing: "border-box",
            }}
        >
            <div className="flex mb-3 gap-2">
                <Button
                    label="Back"
                    onClick={() => navigate("/")}
                    severity="secondary"
                    className="w-3"
                    icon="pi pi-arrow-left"
                />
            </div>
            <Panel header="Not found">
                Leaderboard {uuid} was not found!
            </Panel>
        </div>
    }

    return (
        <div
            style={{
                maxWidth: "800px",
                margin: "0 auto",
                padding: "1rem",
                boxSizing: "border-box",
            }}
        >
            <ConfirmPopup/>
            <Toast/>
            {<LeaderboardEditSidebar visible={leaderboardSidebarVisible}
                                     onHide={() => setLeaderboardSidebarVisible(false)}
                                     leaderboard={leaderboard} onSave={onLeaderboardSave}/>}
            {<EntryEditSidebar visible={entrySidebarVisible} onHide={() => setEntrySidebarVisible(false)}
                               leaderboard={leaderboard} onSave={onEntrySave}/>}
            <div className="flex mb-3 gap-2">
                <Button
                    label="Back"
                    onClick={() => navigate("/")}
                    severity="secondary"
                    className="w-4 sm:w-3"
                    icon="pi pi-arrow-left"
                />
                <div className="flex-grow-1"/>
                <Button
                    loading={isLoading}
                    onClick={() => setLeaderboardSidebarVisible(true)}
                    className="w-2 sm:w-1"
                    tooltip="Edit leaderboard"
                    icon="pi pi-pencil"
                />
                <Button
                    loading={isLoading}
                    severity="danger"
                    onClick={(event) => confirmDelete(event.currentTarget)}
                    className="w-2 sm:w-1"
                    tooltip="Delete leaderboard"
                    icon="pi pi-trash"
                />
            </div>

            <Panel className="mb-3" header="Details">
                <div className="flex flex-column gap-6">
                    <div
                        className="flex flex-wrap px-2 align-items-center justify-content-between p-0 w-full transition-duration-200">
                        <div className="flex align-items-center">
                            {(() => {
                                switch (leaderboard?.sorting) {
                                    case Sorting.Ascending:
                                        return <i className="pi pi-sort-amount-up mr-2"></i>;
                                    case Sorting.Descending:
                                        return <i className="pi pi-sort-amount-down mr-2"></i>;
                                    default:
                                        return null;
                                }
                            })()}
                            <span className="font-bold">{leaderboard?.name}</span>
                        </div>
                        <div className="flex align-items-center">
                            <span className="font-bold text-color-secondary text-xs">
                              {leaderboard?.id}
                            </span>
                        </div>

                    </div>
                    <div className="flex align-items-center gap-2">
                        <span className="p-float-label flex-grow-1">
                            <InputText
                                className="w-full"
                                id="secret"
                                disabled={true}
                                value={leaderboard.secret}/>
                            <label htmlFor="secret">Secret</label>
                        </span>
                        <Button
                            icon="pi pi-copy"
                            className="p-button-text p-button-plain"
                            onClick={() => {
                                navigator.clipboard.writeText(leaderboard.secret);
                            }}/>
                    </div>
                </div>
            </Panel>

            <Panel className="mb-3" header="Entries">
                <EntryView entries={entries} onEntryDeleted={onEntryDelete}/>
                <div className="mb-3"/>
                <div className="flex justify-content-end">
                    <Button
                        label="Add Entry"
                        className="w-6 sm:w-3"
                        icon="pi pi-plus"
                        onClick={() => setEntrySidebarVisible(true)}
                    />
                </div>
            </Panel>
            {(
                <Panel header="Implementation" collapsed={true} toggleable={true}>
                    <LeaderboardGdImplementation leaderboard={leaderboard}/>
                </Panel>
            )}
        </div>
    );
}

export default LeaderboardDetails;
